import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import configData from '../config.json';

/*
 * Ask the venue whether the basket can actually be served — INVENTORY-SPEC.md §4.1.
 *
 * WHY THIS EXISTS: stock is deducted at checkout, so without this a guest can put
 * five of something there are four of into the basket, fill in their details,
 * reach the payment step, and only then be told. The cart is client-side and the
 * guest menu deliberately carries no stock numbers, so the client cannot work it
 * out on its own — it has to ask.
 *
 * WHAT THIS IS NOT: the guard. Stock moves between a cart check and a checkout,
 * and the reservation at checkout is the only place the answer is decided. This
 * is a courtesy that moves the bad news earlier.
 *
 * The endpoint answers a bare ok for a basket it can serve and returns numbers
 * ONLY for the lines that are short, so it cannot be walked to read a venue's
 * stock. It is rate limited per IP per venue, hence the debounce below: a guest
 * holding down the + button must not fire one request per click.
 */

// Long enough that a run of + clicks is one request, short enough that the answer
// lands before the guest has moved on.
const DEBOUNCE_MS = 450;

export default function useStockCheck({ restaurantId, lines, enabled = true }) {
  const [soldOut, setSoldOut] = useState([]);
  const [message, setMessage] = useState('');
  const [checking, setChecking] = useState(false);

  // Only the newest answer may write state: a slow reply for an old basket
  // arriving after a fast one for the current basket would otherwise resurrect a
  // warning the guest has already fixed.
  const requestSeq = useRef(0);
  const timer = useRef(null);

  const serialised = JSON.stringify(
    (lines || []).map((line) => ({
      _id: line._id,
      qty: line.qty,
      extras: (line.extras || []).map((e) => ({ _id: e._id, qty: e.qty })),
    })),
  );

  const clear = useCallback(() => {
    setSoldOut([]);
    setMessage('');
  }, []);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);

    const parsed = JSON.parse(serialised);
    if (!enabled || !restaurantId || parsed.length === 0) {
      clear();
      setChecking(false);
      return undefined;
    }

    timer.current = setTimeout(async () => {
      const seq = ++requestSeq.current;
      setChecking(true);
      try {
        const res = await axios.post(
          `${configData.SERVER_URL}/inventory/check`,
          { restaurantId, order: parsed },
          { validateStatus: () => true },
        );
        if (seq !== requestSeq.current) return;

        // Fail open on anything unexpected — including the 429 from holding down
        // the + button. A cart check that cannot answer must never be the reason
        // a guest cannot order; checkout will decide properly.
        if (res.status !== 200 || !res.data || res.data.ok !== false) {
          clear();
          return;
        }
        setSoldOut(Array.isArray(res.data.soldOut) ? res.data.soldOut : []);
        setMessage(res.data.Message || '');
      } catch (ex) {
        if (seq === requestSeq.current) clear();
      } finally {
        if (seq === requestSeq.current) setChecking(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [restaurantId, serialised, enabled, clear]);

  return {
    soldOut,
    // The ids of the lines that cannot be served, for marking them in the cart.
    soldOutIds: soldOut.map((item) => String(item._id)),
    message,
    checking,
    hasIssue: soldOut.length > 0,
  };
}

/*
 * The one-shot form, for the moment a guest presses "add to order".
 *
 * Not a hook — it is called from an event handler, once, and the answer is shown
 * in the product modal. The debounced hook above is for the cart, which changes
 * continuously; this is for a single deliberate action.
 *
 * `quantity` is what the basket would hold of this item AFTER the add, not what
 * the stepper says: the same item can already be on another line, and the venue's
 * stock is a limit on the item, not on one line of it.
 *
 * Fails OPEN and returns no message: if the venue cannot be reached, the guest
 * adds the item and the cart check — and ultimately the reservation at
 * checkout — will have the last word.
 */
export const checkStockForAdd = async ({ restaurantId, item, quantity, extras }) => {
  if (!restaurantId || !item || !item._id || !(quantity > 0)) return { message: '' };
  try {
    const res = await axios.post(
      `${configData.SERVER_URL}/inventory/check`,
      {
        restaurantId,
        order: [
          {
            _id: item._id,
            qty: quantity,
            extras: (extras || [])
              .filter((extra) => extra.quantity > 0)
              .map((extra) => ({ _id: extra.id, qty: extra.quantity })),
          },
        ],
      },
      { validateStatus: () => true },
    );
    if (res.status !== 200 || !res.data || res.data.ok !== false) return { message: '' };
    return { message: res.data.Message || '', soldOut: res.data.soldOut || [] };
  } catch (ex) {
    return { message: '' };
  }
};
